import { ExternalLink } from '../components/ui/ExternalLink'
import { Panel } from '../components/ui/Panel'
import { CHAIN_HISTORY_START, LINKS } from '../config'

/**
 * Static methodology content. Everything below mirrors the actual engine
 * (packages engine/ + optimizer/) — formulas are quoted from the code that
 * runs, not idealized. Where the engine adapts to the chain's short history,
 * this page says so explicitly.
 */

function CodeBlock({ children }: { children: string }) {
  return (
    <pre className="overflow-x-auto border border-crt-dim/60 bg-crt-bg p-3 text-xs leading-relaxed text-crt-lime">
      <code>{children}</code>
    </pre>
  )
}

export function Methodology() {
  return (
    <div className="space-y-6">
      <Panel title="strategy">
        <div className="space-y-3 text-sm leading-relaxed">
          <p>
            Agentic Stonker is a long-only, cash-buffered momentum strategy
            over tokenized stocks on Robinhood Chain, executed autonomously
            from an ERC-6551 token-bound account. Each cycle it: (1) screens every
            candidate stock token through a six-stage filter ladder, (2)
            scores survivors with a volatility-adjusted momentum composite,
            (3) sizes positions with a mean-variance (MPT) optimizer under
            hard caps, and (4) nets the target against current holdings and
            executes only trades that clear minimum-size and slippage
            thresholds — preferring 0x RFQ, falling back to AMMs.
          </p>
          <p>
            Universe definition is conservative: a token is only considered
            canonical if it is an ERC-20 whose name matches{' '}
            <code className="text-crt-orange">
              "&lt;Name&gt; • Robinhood Token"
            </code>{' '}
            behind the shared Stock implementation BeaconProxy, with an icon
            served from cdn.robinhood.com. Everything else is ignored, no
            matter how tempting the ticker looks.
          </p>
        </div>
      </Panel>

      <Panel title="an honest note on data depth">
        <div className="space-y-3 text-sm leading-relaxed">
          <p className="text-crt-orange">
            Robinhood Chain stock-token history begins {CHAIN_HISTORY_START}.
            That is weeks, not years.
          </p>
          <p>
            Every statistic on this site — momentum scores, covariance
            estimates, benchmark comparisons — is computed over short,
            adaptive windows because longer ones do not exist yet. The engine
            shrinks its lookbacks to the data available (see the formula
            below: <code>W_long = min(60, N−1)</code>), requires fewer
            trading days while the chain is young (10 days now, stepping up
            to 20 once the chain is 45+ days old), and the optimizer applies
            Ledoit–Wolf shrinkage precisely because sample covariance over a
            few weeks of daily returns is unreliable. Treat every number here
            as a low-confidence estimate that will firm up as history
            accumulates.
          </p>
        </div>
      </Panel>

      <Panel title="filter ladder">
        <ol className="list-inside list-decimal space-y-2 text-sm">
          <li>
            <span className="text-crt-lime">canonical</span> — token identity
            check (name pattern, shared Stock BeaconProxy implementation,
            Robinhood CDN icon).
          </li>
          <li>
            <span className="text-crt-lime">history sufficiency</span> —
            minimum trading days (adaptive: 10 young chain / 20 mature) and
            ≥85% coverage of possible weekdays since listing.
          </li>
          <li>
            <span className="text-crt-lime">volume</span> — median of last 7
            daily USD volumes ≥ $1M and latest 24h volume ≥ $500k.
          </li>
          <li>
            <span className="text-crt-lime">liquidity</span> — measured RFQ
            probe impact ≤ 75 bps or AMM impact ≤ 50 bps / pool ≥ $500k; with
            no impact evidence, volume ≥ $2M admits the token flagged{' '}
            <code>liquidity_unverified</code> (its position cap is halved).
          </li>
          <li>
            <span className="text-crt-lime">staleness</span> — last trade
            within 24h, ≤30% zero-volume weekdays in the last 30, and the
            last 3 closes not all identical.
          </li>
          <li>
            <span className="text-crt-lime">anomaly</span> — any |daily
            return| &gt; 50% or intraday range &gt; 100% of close fails the
            token for the cycle. Issuer controls are also respected here: a
            paused token or a blocklisted counterparty is excluded outright,
            and ScaledUI multiplier updates (how splits arrive on this chain)
            are normalized before any return is computed so a 10:1 split does
            not read as a −90% day.
          </li>
        </ol>
      </Panel>

      <Panel title="momentum composite">
        <p className="mb-2 text-sm">
          Exactly as implemented in <code>engine/src/momentum.ts</code>{' '}
          (N = trading days available; TR = total return over the window):
        </p>
        <CodeBlock>{`W_long  = min(60, N-1)
W_short = min(20, max(5, floor(W_long/3)))
raw     = 0.5*TR(W_short) + 0.5*TR(W_long)
sigma   = max(0.10, stdev(last W_long daily returns) * sqrt(252))
ra      = raw / sigma
gate    = P > MA(min(20,N))
       && (N >= 50 ? P > MA(50) : true)
       && (N >= 15 ? RSI(14) in [30, 80] : true)
c       = clip(ADV(min(5,N)) / ADV(min(20,N)), 0.5, 1.5)
S       = gate ? z(ra) + 0.25 * z(ln c) : -Infinity`}</CodeBlock>
        <p className="mt-2 text-xs text-crt-dim">
          z-scores are cross-sectional over gate-passing tokens only,
          winsorized at ±3, computed in ascending token-address order for
          determinism. Tokens with fewer than 2 trading days fail the gate by
          construction.
        </p>
      </Panel>

      <Panel title="portfolio construction (mpt objective)">
        <p className="mb-2 text-sm">
          Position sizes come from a deterministic mean-variance program
          solved by SLSQP with analytic gradients
          (<code>optimizer/optimizer.py</code>):
        </p>
        <CodeBlock>{`maximize   mu' w  -  (gamma/2) w' SIGMA w  -  lambda_tc * || w - w_prev ||_1
subject to sum(w) = 1 - cash_reserve
           0 <= w_i <= w_max            (w_max halved if liquidity_unverified)
           sum |w_i - w_prev_i| <= 2 * turnover_cap

gamma = 5.0    lambda_tc = 0.001    cash_reserve = 0.02
w_max = 0.20   w_min = 0.02 (post-solve zeroing)   turnover_cap = 0.25`}</CodeBlock>
        <p className="mt-2 text-xs text-crt-dim">
          SIGMA is a Ledoit–Wolf shrunk covariance of aligned daily returns,
          annualized at 252 days plus a 1e-6 ridge. Expected returns mu map
          cross-sectional momentum z-scores through a fixed information
          coefficient (0.05). The L1 terms use a smooth approximation
          |x| ≈ sqrt(x² + 1e-10) so the problem stays differentiable and
          deterministic.
        </p>
      </Panel>

      <Panel title="risk controls">
        <ul className="list-inside space-y-1.5 text-sm">
          <li>&gt; long-only; no leverage, no shorting, no derivatives.</li>
          <li>&gt; per-asset cap 20% of NAV (10% if liquidity unverified).</li>
          <li>&gt; permanent cash reserve in USDG (2% floor, configurable).</li>
          <li>&gt; one-way turnover cap of 25% of NAV per rebalance.</li>
          <li>
            &gt; minimum trade size ($50 or 0.25% of NAV) — dust drift is
            left alone rather than churned.
          </li>
          <li>
            &gt; clip sizing: no single execution larger than 10% of the
            token's 24h USD volume.
          </li>
          <li>
            &gt; drift-triggered rebalancing: 5pp absolute, 25% relative to
            target, or 10% total one-sided portfolio drift.
          </li>
          <li>
            &gt; daily loss limit and a hard drawdown kill threshold — either
            flattens to USDG and halts trading until the operator intervenes.
          </li>
          <li>
            &gt; operator kill switch, and a mode ladder (paper → testnet →
            approval-gated → live) so nothing trades real assets before its
            behavior has been observed in every prior mode.
          </li>
          <li>
            &gt; issuer-control awareness: tokens carry role-gated pause(),
            an address blocklist, and adminBurn clawback. The agent treats
            these as hard exclusions, not inconveniences to route around.
          </li>
        </ul>
      </Panel>

      <Panel title="optimizer fallback ladder">
        <p className="mb-2 text-sm">
          When the primary QP cannot run or fails, sizing degrades in
          deterministic steps (each step is tagged in the cycle diagnostics):
        </p>
        <CodeBlock>{`universe empty                 -> all cash (USDG)
universe <= 2 tokens           -> equal weight, capped at 25% each
aligned history < 2 rows       -> equal weight (insufficient history)
SLSQP failure                  -> min-variance (same QP, mu dropped)
min-variance failure           -> capped inverse-volatility weights
all weights below w_min        -> all cash (USDG)`}</CodeBlock>
        <p className="mt-2 text-xs text-crt-dim">
          Execution has its own ladder: 0x RFQ first; if the quote expires or
          degrades beyond the slippage budget, Uniswap, then 1inch; if no
          venue clears the budget, the trade is skipped and logged — never
          forced.
        </p>
      </Panel>

      <Panel title="references">
        <ul className="space-y-1 text-sm">
          <li>
            <ExternalLink href={LINKS.stockTokenTerms}>
              Robinhood stock token terms
            </ExternalLink>
          </li>
        </ul>
      </Panel>
    </div>
  )
}
